import {
  Check,
  ChevronDown,
  Copy,
  Download,
  ExternalLink,
  FileText,
  Video,
} from 'lucide-react'
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Menu, MenuContent, MenuItem, MenuTrigger } from '@/components/ui/overlays'
import { AddNote } from './AddNote'
import { NoteCard } from './NoteCard'
import { cachedNotes, getMeetingDetail, meetingTranscript } from '@/data/meetings'
import type { Meeting } from '@/data/types'

const ASSET_TRIGGER =
  'inline-flex h-6 items-center gap-1 rounded-[var(--radius-sm)] px-1.5 text-2xs ' +
  'text-[var(--color-fg-muted)] outline-none transition-colors duration-100 ' +
  'hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-fg)] disabled:opacity-40'

function download(url: string, filename: string): void {
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
}

function downloadText(filename: string, text: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/markdown;charset=utf-8' }))
  download(url, filename)
  URL.revokeObjectURL(url)
}

function baseFilename(meeting: Meeting): string {
  const date = new Date(meeting.startsAt).toISOString().slice(0, 10)
  const slug = meeting.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
  return `${date}-${slug}`
}

/**
 * The unfolded meeting.
 *
 * Only notes live here. The old panel led with a summary and the
 * raw transcript chunks, which put the useful, edited output below two screens
 * of material nobody read. The transcript is still one click away as a
 * download or a copy — it is just no longer the page.
 */
export function MeetingDetail({
  meeting,
  onAddNote,
  onAddBlankNote,
  onAddPromptNote,
  onEditNote,
  onToggleNoteShared,
  onRemoveNote,
  busy,
}: {
  meeting: Meeting
  onAddNote: (templateId: string) => void
  onAddBlankNote: (title: string) => void
  onAddPromptNote: (prompt: string) => void
  onEditNote: (noteId: string, body: string) => void
  onToggleNoteShared: (noteId: string, shared: boolean) => void
  onRemoveNote: (noteId: string) => void
  busy?: boolean
}) {
  const [copied, setCopied] = useState(false)

  // Notes are not on the list row — the row carries a count, this carries the
  // notes themselves, fetched when the row opens.
  const detail = useQuery({
    queryKey: ['meeting', meeting.id],
    queryFn: () => getMeetingDetail(meeting.id),
  })
  const notes = detail.data?.notes ?? cachedNotes(meeting.id)

  const copyTranscript = async () => {
    await navigator.clipboard.writeText(await meetingTranscript(meeting))
    setCopied(true)
    setTimeout(() => setCopied(false), 1600)
  }

  // No background of its own: the open block already supplies it, and two
  // tints stacked on each other was most of why the seam looked muddy.
  return (
    <div className="space-y-2.5 border-t border-[var(--color-border)] px-3 py-3">
      <div className="flex flex-wrap items-center gap-1">
        {/* One button per asset, each opening its two things to do with it.
            Four flat buttons made the panel look like a toolbar; the asset is
            the noun, and download / copy / open are what you do to it. */}
        {meeting.hasTranscript ? (
          <Menu>
            <MenuTrigger className={ASSET_TRIGGER}>
              {copied ? <Check className="size-3" /> : <FileText className="size-3" />}
              {copied ? 'Copied' : 'Transcript'}
              <ChevronDown className="size-2.5 opacity-60" />
            </MenuTrigger>
            <MenuContent align="start">
              <MenuItem
                className="text-xs"
                onClick={() => {
                  void meetingTranscript(meeting).then((text) =>
                    downloadText(`${baseFilename(meeting)}.md`, text)
                  )
                }}
              >
                <Download className="size-3.5" />
                Download as Markdown
              </MenuItem>
              <MenuItem className="text-xs" onClick={() => void copyTranscript()}>
                <Copy className="size-3.5" />
                Copy to clipboard
              </MenuItem>
            </MenuContent>
          </Menu>
        ) : null}

        {meeting.hasVideo ? (
          <Menu>
            <MenuTrigger className={ASSET_TRIGGER}>
              <Video className="size-3" />
              Video
              <ChevronDown className="size-2.5 opacity-60" />
            </MenuTrigger>
            <MenuContent align="start">
              <MenuItem
                className="text-xs"
                disabled={!meeting.videoUrl}
                onClick={() => {
                  if (meeting.videoUrl) download(meeting.videoUrl, `${baseFilename(meeting)}.mp4`)
                }}
              >
                <Download className="size-3.5" />
                Download recording
              </MenuItem>
              <MenuItem
                className="text-xs"
                disabled={!meeting.videoUrl}
                onClick={() => {
                  if (meeting.videoUrl) window.open(meeting.videoUrl, '_blank', 'noopener')
                }}
              >
                <ExternalLink className="size-3.5" />
                Open in a new tab
              </MenuItem>
              {meeting.videoUrl ? null : (
                <p className="px-2 pt-1.5 pb-1 text-2xs text-[var(--color-fg-subtle)]">
                  Video storage is not connected yet.
                </p>
              )}
            </MenuContent>
          </Menu>
        ) : null}

        <div className="ml-auto">
          <AddNote
            notes={notes}
            busy={busy}
            onAddFromTemplate={onAddNote}
            onAddPrompt={onAddPromptNote}
            onAddBlank={onAddBlankNote}
          />
        </div>
      </div>

      {notes.length > 0 ? (
        <div className="space-y-1.5">
          {notes.map((note) => (
            <NoteCard
              key={note.id}
              note={note}
              busy={busy}
              onEdit={(body) => onEditNote(note.id, body)}
              onToggleShared={(shared) => onToggleNoteShared(note.id, shared)}
              onRemove={() => onRemoveNote(note.id)}
            />
          ))}
        </div>
      ) : (
        <p className="px-1 py-4 text-center text-2xs text-[var(--color-fg-subtle)]">
          {meeting.status === 'processing'
            ? 'Notes are written once the transcript is ready.'
            : meeting.status === 'failed'
              ? 'This meeting failed to process, so nothing could be extracted.'
              : 'No notes yet.'}
        </p>
      )}
    </div>
  )
}
